import { Link } from "@tanstack/react-router"
import { getChartPalette, getFillColor } from "design-system/lib/chart-utils"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "design-system/molecules/Chart"
import { cn } from "design-system/utils"
import { motion } from "framer-motion"
import { capitalize, ensure, formatDate, isNil } from "lopermut"
import { RadarIcon } from "lucide-react"
import { useCampaignsImpactQuery } from "modules/campaigns/hooks/useCampaignsImpactQuery"
import { Loading } from "modules/shared/components/Loading"
import { ACTIVITY_BAR_SIZE } from "modules/shared/lib/charts"
import { ReactNode, useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts"

type Props = {
  data: ReturnType<typeof useCampaignsImpactQuery>["data"]
  isPending: boolean
  dataKey: "reached" | "engaged" | "achieved" | "insights"
  showLegend?: boolean
  action?: ReactNode
  containerClassName?: string
  colorOverride?: string
}

export const CampaignImpactChart = ({
  data,
  isPending,
  dataKey,
  showLegend = true,
  action,
  containerClassName,
  colorOverride,
}: Props) => {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const noData = !isPending && data && data.stats.reduce((a, b) => a + b[dataKey], 0) === 0

  const palette = useMemo(() => getChartPalette("primary", "stack", 4), [])

  const campaignMap = useMemo(() => {
    if (!data?.campaigns) return {}
    return data.campaigns.reduce(
      (acc, campaign) => {
        acc[campaign.id] = {
          id: campaign.id,
          name: campaign.name || t("campaigns.untitled"),
          hasName: !!campaign.name,
        }
        return acc
      },
      {} as Record<string, { id: string; name: string; hasName: boolean }>,
    )
  }, [data?.campaigns, t])

  const { topCampaignIds, otherCampaignIds } = useMemo(() => {
    if (!data?.campaigns || !data.stats) return { topCampaignIds: [], otherCampaignIds: [] }

    const campaignTotals = data.campaigns
      .map((campaign) => {
        let total = 0
        let hasValue = false
        for (const stat of data.stats) {
          const value = stat.campaigns[campaign.id]?.[dataKey] || 0
          total += value
          if (value > 0) hasValue = true
        }
        return { id: campaign.id, total, hasValue }
      })
      .filter((c) => c.hasValue)

    const sortedCampaigns = [...campaignTotals].sort((a, b) => b.total - a.total)
    const topCampaignIds = sortedCampaigns.slice(0, 4).map((c) => c.id)
    const otherCampaignIds = sortedCampaigns.slice(4).map((c) => c.id)

    return { topCampaignIds, otherCampaignIds }
  }, [data?.campaigns, data?.stats, dataKey])

  const chartData = useMemo(() => {
    let maxReached = 0
    let maxInsights = 0

    const chartData = data?.stats.map((item) => {
      let formattedDate = null
      try {
        formattedDate = item.date ? formatDate(item.date, "MMM d") : null
      } catch (error) {
        console.error(error)
      }

      if (noData) {
        const value = Math.ceil(Math.random() * 80)

        return {
          date: formattedDate,
          [dataKey]: value,
          total: value,
          reachedPercentage: Math.ceil(Math.random() * 15), // Lower percentage for daily share
          engagementPercentage: Math.ceil(Math.random() * 100),
          conversionPercentage: Math.ceil(Math.random() * 100),
          insightsPercentage: Math.ceil(Math.random() * 100),
        }
      }

      const campaignData: {
        top1: number
        top2: number
        top3: number
        top4: number
        others: number
      } = { top1: 0, top2: 0, top3: 0, top4: 0, others: 0 }

      let total = 0

      if (item.campaigns) {
        for (let i = 0; i < topCampaignIds.length; i++) {
          if (isNil(topCampaignIds[i])) continue

          const value = item.campaigns[ensure(topCampaignIds[i])]?.[dataKey] || 0
          campaignData[`top${i + 1}` as keyof typeof campaignData] = value
          total += value
        }

        for (const id of otherCampaignIds) {
          const value = item.campaigns[id]?.[dataKey] || 0
          campaignData.others += value
          total += value
        }
      }

      // Calculate percentages from raw data
      const engagementPercentage = item.reached ? ((item.engaged ?? 0) / item.reached) * 100 : 0
      const conversionPercentage = item.engaged ? ((item.achieved ?? 0) / item.engaged) * 100 : 0

      maxReached = Math.max(maxReached, item.reached)
      maxInsights = Math.max(maxInsights, item.insights)

      return {
        date: formattedDate,
        total,
        insights: item.insights,
        engagementPercentage,
        conversionPercentage,
        ...campaignData,
      }
    })

    const chartDataWithReachedPercentage = chartData?.map((item) => {
      return {
        ...item,
        reachedPercentage: (maxReached > 0 ? (item.total ?? 0) / maxReached : 0) * 100,
        insightsPercentage: (maxInsights > 0 ? Number(item.insights ?? 0) / maxInsights : 0) * 100,
      }
    })

    return chartDataWithReachedPercentage
  }, [data?.stats, noData, dataKey, topCampaignIds, otherCampaignIds])

  useEffect(() => {
    if (containerRef.current && chartData?.length) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth
    }
  }, [chartData?.length])

  if (isPending) {
    return <Loading />
  }

  return (
    <div className="flex flex-col gap-2 w-full relative">
      <div className="flex flex-row items-end justify-between">
        {showLegend && (
          <ActivityTooltipInfo
            campaignMap={campaignMap}
            topCampaignIds={topCampaignIds}
            showOthers={otherCampaignIds.length > 0}
          />
        )}
        <div className="ml-auto">{action}</div>
      </div>
      {noData && (
        <div className="flex flex-col items-center justify-center h-full absolute inset-0 z-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <RadarIcon className="size-10 text-muted-foreground mb-3" strokeWidth={1.5} />
          </motion.div>
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <h2 className="text-md text-foreground text-stroke">
              {t("campaigns.detail.activity.noData.title")}
            </h2>
          </motion.div>
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <p className="text-sm text-muted-foreground text-stroke-sm">
              {t("campaigns.detail.activity.noData.description")}
            </p>
          </motion.div>
        </div>
      )}
      <div
        ref={containerRef}
        className={cn(
          "w-full overflow-x-auto",
          noData && "pointer-events-none grayscale opacity-15",
        )}
      >
        <ChartContainer
          config={{}}
          className={cn("aspect-auto h-[180px]", containerClassName)}
          style={{
            width: `${(chartData?.length ?? 0) * ACTIVITY_BAR_SIZE}px`,
            minWidth: "100%",
          }}
        >
          <ComposedChart accessibilityLayer data={chartData} margin={{ bottom: 10 }}>
            <CartesianGrid vertical={false} yAxisId="bars" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
            />
            <YAxis
              yAxisId="bars"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              width={30}
              allowDataOverflow
              hide
            />
            <YAxis
              yAxisId="percentage"
              orientation="right"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              width={30}
              domain={[0, 102]}
              allowDataOverflow
              hide
            />
            <ChartTooltip
              content={(props: TooltipContentProps) => {
                // Map campaign IDs to their names using the campaign map
                if (!props.active || !props.payload) return null

                const enhancedPayload = props.payload.map((entry) => {
                  const key = entry.dataKey?.toString()

                  // Format percentage values
                  if (key === "reachedPercentage") {
                    return {
                      ...entry,
                      name: t("campaigns.detail.activity.dailyShare"),
                      value: `${Math.round((entry.value ?? 0) as number)}%`,
                      color: "var(--muted-foreground)",
                    }
                  }
                  if (key === "engagementPercentage") {
                    return {
                      ...entry,
                      name: t("campaigns.detail.activity.engagementRate"),
                      value: `${Math.round((entry.value ?? 0) as number)}%`,
                      color: "var(--muted-foreground)",
                    }
                  }
                  if (key === "conversionPercentage") {
                    return {
                      ...entry,
                      name: t("campaigns.detail.activity.conversionRate"),
                      value: `${Math.round((entry.value ?? 0) as number)}%`,
                      color: "var(--muted-foreground)",
                    }
                  }
                  if (key === "insightsPercentage") {
                    return {
                      ...entry,
                      name: t("campaigns.detail.activity.insightsRate"),
                      value: `${Math.round((entry.value ?? 0) as number)}%`,
                      color: "var(--muted-foreground)",
                    }
                  }

                  // Format campaign data
                  if (key === "others") {
                    return {
                      ...entry,
                      name: "Others",
                    }
                  }
                  if (key?.startsWith("top")) {
                    const index = parseInt(key.replace("top", "")) - 1
                    const campaignId = topCampaignIds[index]
                    if (campaignId) {
                      return {
                        ...entry,
                        name: campaignMap[campaignId]?.name,
                      }
                    }
                  }
                  return entry
                })

                return (
                  <ChartTooltipContent
                    {...props}
                    payload={enhancedPayload}
                    labelFormatter={(label) =>
                      typeof label === "string" ? capitalize(label) : label
                    }
                  />
                )
              }}
            />
            {otherCampaignIds.length > 0 && (
              <Bar
                key="others"
                yAxisId="bars"
                dataKey="others"
                stackId="a"
                fill={colorOverride ?? "var(--neutral)"}
                barSize={ACTIVITY_BAR_SIZE}
                className="transition-all duration-500"
              />
            )}
            {topCampaignIds.map((id, index) => {
              const key = `top${index + 1}` as const
              const fillColor = colorOverride
                ? colorOverride
                : getFillColor(palette[index], `var(--chart-${index + 1})`)
              return (
                <Bar
                  key={key}
                  yAxisId="bars"
                  dataKey={key}
                  stackId="a"
                  fill={fillColor}
                  barSize={ACTIVITY_BAR_SIZE}
                  name={campaignMap[id]?.name}
                  className="transition-all duration-500"
                />
              )
            })}
            {/* Show daily share percentage line when viewing reached customers */}
            {dataKey === "reached" && (
              <Line
                yAxisId="percentage"
                type="monotone"
                dataKey="reachedPercentage"
                stroke="var(--muted-foreground)"
                strokeWidth={2}
                strokeDasharray="2 2"
                dot={false}
                connectNulls={false}
                className="opacity-70"
              />
            )}
            {/* Show engagement percentage line when viewing engaged customers */}
            {dataKey === "engaged" && (
              <Line
                yAxisId="percentage"
                type="monotone"
                dataKey="engagementPercentage"
                stroke="var(--muted-foreground)"
                strokeWidth={2}
                strokeDasharray="2 2"
                dot={false}
                connectNulls={false}
                className="opacity-70"
              />
            )}
            {/* Show conversion percentage line when viewing achieved customers */}
            {dataKey === "achieved" && (
              <Line
                yAxisId="percentage"
                type="monotone"
                dataKey="conversionPercentage"
                stroke="var(--muted-foreground)"
                strokeWidth={2}
                strokeDasharray="2 2"
                dot={false}
                connectNulls={false}
                className="opacity-70"
              />
            )}
            {/* Show insights percentage line when viewing insights captured */}
            {dataKey === "insights" && (
              <Line
                yAxisId="percentage"
                type="monotone"
                dataKey="insightsPercentage"
                stroke="var(--muted-foreground)"
                strokeWidth={2}
                strokeDasharray="2 2"
                dot={false}
                connectNulls={false}
                className="opacity-70"
              />
            )}
          </ComposedChart>
        </ChartContainer>
      </div>
    </div>
  )
}

const ActivityTooltipInfo = ({
  campaignMap,
  topCampaignIds,
  showOthers = true,
}: {
  campaignMap: Record<string, { id: string; name: string; hasName: boolean }>
  topCampaignIds: string[]
  showOthers?: boolean
}) => {
  const palette = getChartPalette("primary", "stack", 4)
  const getColorForIndex = (index: number) =>
    `var(--${palette[index]?.color ?? `chart-${index + 1}`})`

  return (
    <div className="flex items-center gap-4 flex-wrap">
      {topCampaignIds.map((campaignId, index) => {
        const key = `top${index + 1}` as const
        return (
          <div key={key} className="flex items-center gap-1.5">
            <div className="size-2 rounded-full" style={{ background: getColorForIndex(index) }} />
            <Link
              className={cn(
                "text-sm text-muted-foreground hover:text-primary hover:underline",
                campaignMap[campaignId]?.hasName ? "font-normal" : "font-bold",
              )}
              to="/campaigns/$id"
              params={{ id: campaignMap[campaignId]?.id ?? "" }}
              disabled={!campaignMap[campaignId]?.hasName}
            >
              {campaignMap[campaignId]?.name}
            </Link>
          </div>
        )
      })}
      {showOthers && (
        <div className="flex items-center gap-1.5">
          <div className="size-2 rounded-full" style={{ background: "var(--chart-secondary)" }} />
          <span className="text-sm text-muted-foreground">{"Others"}</span>
        </div>
      )}
    </div>
  )
}
